import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import { wrapAngle } from '../labyrinth/mathUtils';
import type { Robot } from '../labyrinth/Robot';
import { SLOT_OFFSETS } from '../rendering/RobotGraphic';
import { SKIN_TIME, type LightmapBindings } from './StylizedShading';
import {
  createRobotChassis, CHASSIS_H, CHASSIS_L, CHASSIS_W, CHASSIS_Y,
} from './robot/RobotChassis';
import {
  createRobotWheel, WHEEL_LEN, WHEEL_R, WHEEL_Y,
  type RobotWheelPart,
} from './robot/RobotWheel';
import {
  createRobotTurret, TURRET_BASE_COLOR, TURRET_H, TURRET_R,
} from './robot/RobotTurret';
import { createRobotBarrel, BARREL_L } from './robot/RobotBarrel';
import {
  createRobotWeapon, WEAPON_L,
  type RobotWeaponPart,
} from './robot/RobotWeapon';

// Composite placement constants — these describe how the parts fit together
// (not the parts themselves), so they live in the rig.
const WHEEL_X_OUT   = CHASSIS_W / 2 + WHEEL_LEN / 2 - TILE_SIZE * 0.01;
const TURRET_Y      = CHASSIS_Y + CHASSIS_H / 2 + TURRET_H / 2;
const HARDPOINT_OFF = TILE_SIZE * 0.8;            // matches EntityBillboards off3D

// State → turret tint. Scene has no Three.js lights (tiles use
// MeshBasicMaterial), so robot state is expressed by mutating the turret
// material color rather than via an emissive channel.
const STATE_TURRET_TINT: Record<string, number> = {
  exploring: TURRET_BASE_COLOR,
  combat:    0x572b1c,
  looting:   0x5d4e1c,
  moving_to_marker: TURRET_BASE_COLOR,
};

// Convention: forward in local space is +X (matches TireTracks3D's
// (cos(angle), sin(angle)) world-X/Z formulation). A Three.js rotation.y
// rotates +X toward -Z, whereas the game's angle rotates +X toward +Z — so
// we apply rotation.y = -angle throughout.

export interface RobotRig {
  readonly root: THREE.Group;
  /** @param t frame time in seconds — drives shader-side LED blinks and conduit flow. */
  update(robot: Robot, t: number): void;
  dispose(): void;
}

export function createRobotRig(lightmap: LightmapBindings): RobotRig {
  const root = new THREE.Group();

  // Chassis + wheels yaw together with the robot body.
  const chassisGroup = new THREE.Group();
  root.add(chassisGroup);

  const chassis = createRobotChassis(lightmap);
  chassisGroup.add(chassis.object);

  const wheelOffsets = [-CHASSIS_L * 0.35, 0, CHASSIS_L * 0.35];
  const buildWheelSide = (sign: 1 | -1): { group: THREE.Group; wheels: RobotWheelPart[] } => {
    const group = new THREE.Group();
    const wheels: RobotWheelPart[] = [];
    for (const fx of wheelOffsets) {
      const w = createRobotWheel(lightmap);
      w.object.position.set(fx, WHEEL_Y, sign * WHEEL_X_OUT);
      group.add(w.object);
      wheels.push(w);
    }
    return { group, wheels };
  };
  const left  = buildWheelSide(-1);
  const right = buildWheelSide(1);
  chassisGroup.add(left.group);
  chassisGroup.add(right.group);

  // Turret pivot (independent yaw). Translation tracks the root; the pivot's
  // y offset perches the turret centre on top of the chassis.
  const turretPivot = new THREE.Group();
  turretPivot.position.y = TURRET_Y;
  root.add(turretPivot);

  const turret = createRobotTurret(lightmap);
  turretPivot.add(turret.object);

  const barrel = createRobotBarrel(lightmap);
  // Extends forward from the turret edge along local +X. The "- 2" sinks the
  // barrel slightly back into the turret rim so the join reads as continuous.
  barrel.object.position.set(TURRET_R + BARREL_L / 2 - 2, 0, 0);
  turretPivot.add(barrel.object);

  // Main weapon slots — created once, hidden by default; the rig shows and
  // places them per occupied non-side slot each frame. Distinct seeds so the
  // four weapons don't blink in unison.
  const mainWeapons: RobotWeaponPart[] = [];
  for (let i = 0; i < 4; i++) {
    const w = createRobotWeapon(lightmap, i + 3);
    w.object.visible = false;
    turretPivot.add(w.object);
    mainWeapons.push(w);
  }

  // Two side-hardpoint pivots (independent yaw). Parented under the turret so
  // their mount positions rotate with the turret, but each pivot adds its own
  // aim on top (mirrors the 2D turretContainer + per-hardpoint rotation in
  // EntityBillboards).
  interface Hardpoint { pivot: THREE.Group; weapon: RobotWeaponPart; }
  const makeHardpoint = (seed: number): Hardpoint => {
    const pivot = new THREE.Group();
    pivot.visible = false;
    const weapon = createRobotWeapon(lightmap, seed);
    weapon.object.position.set(WEAPON_L / 2, 0, 0); // extend forward from pivot
    pivot.add(weapon.object);
    turretPivot.add(pivot);
    return { pivot, weapon };
  };
  const hardpoints: readonly Hardpoint[] = [makeHardpoint(19), makeHardpoint(23)];

  // Wheel rolling state. Spins accumulate so the texture cadence stays
  // continuous across frames; lateral/radius are kept in tile units so the
  // TILE_SIZE factor cancels between speed (tiles/s) and radius.
  const wheelRadiusTiles  = WHEEL_R / TILE_SIZE;
  const wheelLateralTiles = WHEEL_X_OUT / TILE_SIZE;
  let prevT = -1;
  let prevAngle = 0;
  let prevAngleInit = false;
  let leftSpin = 0;
  let rightSpin = 0;

  const update = (robot: Robot, t: number): void => {
    // Shared time uniform — one write animates every skinned material on the
    // robot (and any other opted-in material using the same SKIN_TIME ref).
    SKIN_TIME.value = t;

    root.position.set(robot.displayX * TILE_SIZE, 0, robot.displayY * TILE_SIZE);
    chassisGroup.rotation.y = -robot.angle;
    turretPivot.rotation.y  = -robot.turretAngle;

    // Wheel rolling — skid-steer differential. Game angle increases when the
    // chassis turns CW from above, so the right-side wheel runs slower (or
    // backward) and the left-side runs faster, exactly opposite-signed.
    // Forward rolling about local +X requires negative rotation about local
    // +Z (top of wheel sweeps toward +X).
    const dt = prevT < 0 ? 0 : Math.max(0, t - prevT);
    prevT = t;
    const angularVel = (prevAngleInit && dt > 1e-6)
      ? wrapAngle(robot.angle - prevAngle) / dt
      : 0;
    prevAngle = robot.angle;
    prevAngleInit = true;
    const fwd = robot.currentSpeed;
    const leftFwd  = fwd + angularVel * wheelLateralTiles;
    const rightFwd = fwd - angularVel * wheelLateralTiles;
    leftSpin  -= leftFwd  * dt / wheelRadiusTiles;
    rightSpin -= rightFwd * dt / wheelRadiusTiles;
    for (const w of left.wheels)  w.setSpin(leftSpin);
    for (const w of right.wheels) w.setSpin(rightSpin);

    // State tint on the turret (no lights in scene — color swap instead of emissive).
    turret.setStateColor(STATE_TURRET_TINT[robot.state] ?? STATE_TURRET_TINT['exploring']!);

    // Main weapon slots — show only occupied non-side slots.
    const slots = robot.weaponRenderSlots;
    const slotCount = slots.length;
    const offsets = slotCount > 0 ? SLOT_OFFSETS[Math.min(slotCount, 4) - 1]! : [];
    const sideIdx = robot.getSideSlotIndices();
    for (let i = 0; i < mainWeapons.length; i++) {
      const w = mainWeapons[i]!;
      const slot = slots[i];
      const off = offsets[i];
      const isSide = sideIdx[0] === i || sideIdx[1] === i;
      if (!slot || !off || isSide) {
        w.object.visible = false;
        continue;
      }
      // 2D off.x is "forward", off.y is "lateral" (screen +X, +Y respectively, but in sprite-local
      // frame). In our turret-local frame forward = +X, lateral = +Z.
      w.object.position.set(off.x * HARDPOINT_OFF + WEAPON_L / 2, TURRET_H / 2, off.y * HARDPOINT_OFF);
      w.object.visible = true;
    }

    // Side hardpoints — position by slot, aim independently.
    const sideInfo = robot.sideHardpointRenderInfo;
    for (let h = 0; h < hardpoints.length; h++) {
      const hp = hardpoints[h]!;
      const info = sideInfo[h];
      if (!info || !info.slot) {
        hp.pivot.visible = false;
        continue;
      }
      const off = offsets[info.slotIndex];
      if (!off) { hp.pivot.visible = false; continue; }
      hp.pivot.position.set(off.x * HARDPOINT_OFF, TURRET_H / 2, off.y * HARDPOINT_OFF);
      hp.pivot.rotation.y = robot.turretAngle - info.worldAngle; // world yaw = -worldAngle
      hp.pivot.visible = true;
    }
  };

  const dispose = (): void => {
    chassis.dispose();
    for (const w of left.wheels)  w.dispose();
    for (const w of right.wheels) w.dispose();
    turret.dispose();
    barrel.dispose();
    for (const w of mainWeapons) w.dispose();
    for (const hp of hardpoints) hp.weapon.dispose();
  };

  return { root, update, dispose };
}
